import React from 'react';

const ReportSummary = ({ totals, title = "Report Summary" }) => {
  if (!totals) return null;

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount);
  };

  const formatNumber = (num) => {
    const numValue = parseInt(num) || 0;
    return new Intl.NumberFormat('en-PK').format(numValue);
  };

  const formatPercentage = (num) => {
    const percentValue = parseFloat(num) || 0;
    return `${percentValue.toFixed(2)}%`;
  };

  // Define which fields should be displayed as currency, numbers, or percentages
  const currencyFields = [
    'total_sales', 'total_tax', 'total_purchases', 'total_tax_paid', 
    'total_revenue', 'total_sales_value', 'total_tax_charged', 
    'total_amount_withheld', 'total_sales_tax_applicable', 
    'total_sales_tax_withheld', 'avg_invoice_value', 'avg_rate'
  ];

  const numberFields = [
    'invoice_count', 'times_sold', 'total_quantity', 'submitted_count', 
    'error_count', 'unique_codes_used', 'total_transactions'
  ];

  const percentageFields = [
    'avg_tax_rate_percent'
  ];

  const formatValue = (key, value) => {
    if (value == null || value === '') return '-';
    
    if (currencyFields.includes(key)) {
      return formatCurrency(value);
    } else if (numberFields.includes(key)) {
      return formatNumber(value);
    } else if (percentageFields.includes(key)) {
      return formatPercentage(value);
    } else {
      return String(value);
    }
  };

  // Convert field names to readable labels
  const getFieldLabel = (key) => {
    const labels = {
      invoice_count: 'Total Invoices',
      total_sales: 'Total Sales',
      total_tax: 'Total Tax',
      total_purchases: 'Total Purchases',
      total_tax_paid: 'Total Tax Paid',
      total_revenue: 'Total Revenue',
      total_sales_value: 'Total Sales Value',
      total_tax_charged: 'Total Tax Charged',
      total_amount_withheld: 'Total Amount Withheld',
      total_sales_tax_applicable: 'Total Sales Tax Applicable',
      total_sales_tax_withheld: 'Total Sales Tax Withheld',
      avg_invoice_value: 'Average Invoice Value',
      avg_rate: 'Average Rate',
      avg_tax_rate_percent: 'Average Tax Rate',
      times_sold: 'Times Sold',
      total_quantity: 'Total Quantity',
      submitted_count: 'Submitted',
      error_count: 'Errors',
      unique_codes_used: 'Unique Codes',
      total_transactions: 'Total Transactions'
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter out non-numeric fields and organize by importance
  const importantFields = [
    'invoice_count', 'total_sales', 'total_sales_value', 'total_revenue', 
    'total_purchases', 'total_tax', 'total_tax_charged', 'total_tax_paid'
  ];

  const otherFields = [
    'submitted_count', 'error_count', 'times_sold', 'total_quantity',
    'unique_codes_used', 'total_transactions', 'avg_invoice_value', 
    'avg_rate', 'avg_tax_rate_percent', 'total_amount_withheld',
    'total_sales_tax_applicable', 'total_sales_tax_withheld'
  ];

  const displayFields = [
    ...importantFields.filter(field => totals[field] != null),
    ...otherFields.filter(field => totals[field] != null)
  ];

  if (displayFields.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
      <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
        <span className="mr-2">📊</span>
        {title}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {displayFields.map((field) => {
          const value = totals[field];
          const isImportant = importantFields.includes(field);
          
          return (
            <div 
              key={field} 
              className={`p-4 rounded-lg ${
                isImportant 
                  ? 'bg-white border-2 border-blue-300 shadow-md' 
                  : 'bg-blue-25 border border-blue-200'
              }`}
            >
              <div className="text-sm font-medium text-gray-600 mb-1">
                {getFieldLabel(field)}
              </div>
              <div className={`text-lg font-bold ${
                isImportant ? 'text-blue-700' : 'text-gray-700'
              }`}>
                {formatValue(field, value)}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Additional info */}
      <div className="mt-4 text-xs text-blue-600 bg-blue-100 rounded px-3 py-2">
        💡 Summary calculated from all records in the report. Totals row is also included in the table below.
      </div>
    </div>
  );
};

export default ReportSummary;
